import type { DailyCall, DailyEventObjectTrack } from '@daily-co/daily-js';
import {
  ArrowUpRight,
  Eraser,
  Highlighter,
  Maximize,
  Minimize,
  MousePointer2,
  Pencil,
  PhoneOff,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The staff half of a guided support session: the operator's screen, and the ink
 * drawn over it.
 *
 * THE PROTOCOL
 *
 * Strokes travel over Daily's own data channel (`sendAppMessage`), so there is
 * no signalling server anywhere in this feature. The wire format is shared with
 * dispatch-dashboard's web/assist.js, which is the only consumer:
 *
 *   { kind: 'limo-assist/v1', op: 'stroke', tool, color, width, points: [[x,y]…] }
 *   { kind: 'limo-assist/v1', op: 'cursor', x, y, color }
 *   { kind: 'limo-assist/v1', op: 'clear' }
 *
 * COORDINATES ARE NORMALISED 0..1, AND AGAINST THE VIDEO, NOT THE ELEMENT
 *
 * This is the one thing in the file that silently produces a working-looking
 * feature that points at the wrong place. The <video> is `object-fit: contain`,
 * so unless its aspect ratio exactly matches the pane there are letterbox bars,
 * and a coordinate taken against the element's own box is offset by the bar and
 * scaled wrongly besides. What the operator sees is an arrow a centimetre from
 * the button being discussed -- close enough to look like a bug in the drawing
 * and not in the arithmetic.
 *
 * `contentRect` below computes the drawn area, and everything maps through it.
 * On the operator's side the same 0..1 resolves against their viewport, which is
 * correct because they share a TAB: a tab capture and the viewport are the same
 * rectangle. That is why web/assist.js asks for `preferCurrentTab`.
 */

export type Tool = 'pen' | 'arrow' | 'highlight' | 'laser';

const MESSAGE_KIND = 'limo-assist/v1';

const COLORS = ['#ff3b30', '#0a84ff', '#30d158', '#ffd60a'];
// What a screen reader says for each swatch: "Use #ff3b30" names nothing.
const COLOR_NAMES: Record<string, string> = {
  '#ff3b30': 'red',
  '#0a84ff': 'blue',
  '#30d158': 'green',
  '#ffd60a': 'yellow',
};

/** Mirrors web/assist.js, so a stroke dies at the same moment on both screens. */
const STROKE_TTL_MS = 8000;
const STROKE_FADE_MS = 1500;

/**
 * Sampling. A pointermove per pixel produces a message per pixel; at a screen's
 * width that is a thousand messages for one gesture, and Daily's data channel is
 * not the place to find out what that costs.
 */
const MIN_POINT_DISTANCE = 0.004;
const CURSOR_THROTTLE_MS = 60;

interface Stroke {
  at: number;
  tool: Tool;
  color: string;
  width: number;
  points: [number, number][];
}

/**
 * Where the video is actually painted inside its element.
 *
 * With `object-fit: contain` the picture is centred and letterboxed; this is the
 * rectangle it occupies. Before metadata arrives `videoWidth` is 0, and falling
 * back to the whole box is right: nothing can be drawn yet anyway.
 */
export function contentRect(video: {
  getBoundingClientRect: () => { width: number; height: number };
  videoWidth: number;
  videoHeight: number;
}) {
  const box = video.getBoundingClientRect();
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return { x: 0, y: 0, w: box.width, h: box.height };
  const scale = Math.min(box.width / vw, box.height / vh);
  const w = vw * scale;
  const h = vh * scale;
  return { x: (box.width - w) / 2, y: (box.height - h) / 2, w, h };
}

export function AssistStage({
  roomUrl,
  token,
  tenantKey,
  visible = true,
  onEnded,
}: {
  roomUrl: string;
  token: string;
  tenantKey: string;
  /**
   * False while the console is on another screen: the stage stays mounted, and
   * the call joined, but hidden (components/AssistCallHost.tsx). Coming back
   * re-measures the canvas, whose box was nothing while hidden.
   */
  visible?: boolean;
  onEnded: () => void;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const callRef = useRef<DailyCall | null>(null);

  const strokes = useRef<Stroke[]>([]);
  const drawing = useRef<[number, number][] | null>(null);
  const raf = useRef(0);
  const lastCursor = useRef(0);

  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [status, setStatus] = useState('connecting…');
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const send = useCallback((payload: Record<string, unknown>) => {
    try {
      callRef.current?.sendAppMessage({ kind: MESSAGE_KIND, ...payload }, '*');
    } catch {
      // A dropped stroke is not worth breaking the session over; the operator
      // simply does not see that one mark, and the next one still arrives.
    }
  }, []);

  // ---------------------------------------------------------------- rendering

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const box = video.getBoundingClientRect();
    if (canvas.width !== Math.round(box.width * dpr) || canvas.height !== Math.round(box.height * dpr)) {
      canvas.width = Math.round(box.width * dpr);
      canvas.height = Math.round(box.height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, box.width, box.height);

    const rect = contentRect(video);
    const toPx = (p: [number, number]) => ({ x: rect.x + p[0] * rect.w, y: rect.y + p[1] * rect.h });
    const now = Date.now();

    strokes.current = strokes.current.filter((s) => now - s.at < STROKE_TTL_MS);

    const paint = (stroke: Stroke, alpha: number) => {
      if (!stroke.points.length) return;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = stroke.color;
      ctx.fillStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (stroke.tool === 'highlight') {
        ctx.globalAlpha = alpha * 0.35;
        ctx.lineWidth = stroke.width * 4;
      }

      const first = toPx(stroke.points[0]);
      if (stroke.tool === 'arrow' && stroke.points.length >= 2) {
        const last = toPx(stroke.points[stroke.points.length - 1]);
        const head = Math.max(12, stroke.width * 3.5);
        const angle = Math.atan2(last.y - first.y, last.x - first.x);
        ctx.beginPath();
        ctx.moveTo(first.x, first.y);
        ctx.lineTo(last.x, last.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(last.x - head * Math.cos(angle - Math.PI / 6), last.y - head * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(last.x - head * Math.cos(angle + Math.PI / 6), last.y - head * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(first.x, first.y);
        for (let i = 1; i < stroke.points.length; i++) {
          const p = toPx(stroke.points[i]);
          ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }
      ctx.restore();
    };

    for (const stroke of strokes.current) {
      const age = now - stroke.at;
      const remaining = STROKE_TTL_MS - age;
      paint(stroke, remaining >= STROKE_FADE_MS ? 1 : Math.max(0, remaining / STROKE_FADE_MS));
    }

    // The gesture in progress, drawn at full strength. Rendering it locally
    // rather than waiting for it to come back means the drawer never feels the
    // round trip -- and there is no round trip, since the message is one-way.
    if (drawing.current?.length) {
      paint({ at: now, tool, color, width: 3, points: drawing.current }, 1);
    }

    raf.current = strokes.current.length || drawing.current ? requestAnimationFrame(render) : 0;
  }, [tool, color]);

  const kick = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(render);
  }, [render]);

  // ------------------------------------------------------------------ pointer

  const pointAt = (event: React.PointerEvent): [number, number] | null => {
    const video = videoRef.current;
    if (!video) return null;
    const box = video.getBoundingClientRect();
    const rect = contentRect(video);
    const x = (event.clientX - box.left - rect.x) / rect.w;
    const y = (event.clientY - box.top - rect.y) / rect.h;
    // Outside the picture there is nothing to point AT, and a clamped value
    // would silently land on the edge rather than nowhere.
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return [x, y];
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = pointAt(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === 'laser') {
      send({ op: 'cursor', x: point[0], y: point[1], color });
      return;
    }
    drawing.current = [point];
    kick();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = pointAt(event);
    if (!point) return;

    if (tool === 'laser') {
      const now = Date.now();
      if (now - lastCursor.current < CURSOR_THROTTLE_MS) return;
      lastCursor.current = now;
      send({ op: 'cursor', x: point[0], y: point[1], color });
      return;
    }

    const path = drawing.current;
    if (!path) return;
    const previous = path[path.length - 1];
    const far = Math.hypot(point[0] - previous[0], point[1] - previous[1]) > MIN_POINT_DISTANCE;
    // An arrow is defined by two points, so intermediate ones would only make
    // the head chase the cursor through every sample.
    if (tool === 'arrow') path[1] = point;
    else if (far) path.push(point);
    kick();
  };

  const onPointerUp = () => {
    const path = drawing.current;
    drawing.current = null;
    if (!path || path.length === 0) return;
    const stroke: Stroke = { at: Date.now(), tool, color, width: 3, points: path };
    strokes.current.push(stroke);
    send({ op: 'stroke', tool, color, width: 3, points: path });
    kick();
  };

  const clear = () => {
    strokes.current = [];
    drawing.current = null;
    send({ op: 'clear' });
    kick();
  };

  // --------------------------------------------------------------- full screen

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    // The whole STAGE, not the <video>: the ink canvas and the toolbar are its
    // siblings, so a full-screen video would leave nothing to draw with. A
    // refusal leaves the in-page stage exactly as it was, which is the right
    // fallback.
    void stageRef.current?.requestFullscreen().catch(() => {});
  };

  // Read back from the document rather than set by the button: Esc and the
  // browser's own controls leave full screen without the button knowing.
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // --------------------------------------------------------------------- call

  useEffect(() => {
    let cancelled = false;
    let call: DailyCall | null = null;

    // Dynamic import so daily-js lands in its own chunk: the console is opened
    // every day and a support session is not, so ~270KB has no business in the
    // bundle that draws the incident board.
    void (async () => {
      try {
        const Daily = (await import('@daily-co/daily-js')).default;
        if (cancelled) return;

        // Staff watch and draw; they do not appear. No camera, no microphone.
        call = Daily.createCallObject({ audioSource: false, videoSource: false });
        callRef.current = call;

        call.on('track-started', (event?: DailyEventObjectTrack) => {
          if (!event?.participant || event.participant.local) return;
          if (event.track.kind !== 'video') return;
          const video = videoRef.current;
          if (!video) return;
          video.srcObject = new MediaStream([event.track]);
          void video.play().catch(() => {});
          setSharing(true);
          setStatus('sharing');
        });

        call.on('track-stopped', (event?: DailyEventObjectTrack) => {
          if (!event?.participant || event.participant.local) return;
          setSharing(false);
          setStatus('the operator stopped sharing');
        });

        call.on('participant-left', () => {
          setSharing(false);
          setStatus('the operator left');
        });

        call.on('error', (event?: { errorMsg?: string }) => {
          setError(event?.errorMsg || 'the call failed');
        });

        await call.join({ url: roomUrl, token });
        if (cancelled) return;
        setStatus('waiting for the operator to share…');
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : String(caught));
      }
    })();

    return () => {
      cancelled = true;
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
      callRef.current = null;
      // leave() then destroy(): destroying without leaving leaves the operator
      // looking at a participant who is present and doing nothing.
      void call?.leave().catch(() => {});
      call?.destroy();
    };
  }, [roomUrl, token]);

  // The canvas tracks the video's box, which changes with the window and with
  // the first frame's aspect ratio.
  useEffect(() => {
    if (visible) kick();
  }, [visible, kick]);

  useEffect(() => {
    const onResize = () => kick();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [kick]);

  const tools: { id: Tool; icon: typeof Pencil; label: string }[] = [
    { id: 'pen', icon: Pencil, label: 'Draw' },
    { id: 'arrow', icon: ArrowUpRight, label: 'Arrow' },
    { id: 'highlight', icon: Highlighter, label: 'Highlight' },
    { id: 'laser', icon: MousePointer2, label: 'Pointer' },
  ];

  return (
    <div ref={stageRef} className="assist-stage">
      <div className="assist-bar">
        <span className="badge badge-accent">{tenantKey}</span>
        <span className={`assist-status${sharing ? ' is-live' : ''}`} role="status">
          {status}
        </span>
        <span className="wt-spacer" />

        {tools.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={tool === id}
            className={`btn btn-icon${tool === id ? ' is-active' : ''}`}
            onClick={() => setTool(id)}
          >
            <Icon size={15} />
          </button>
        ))}

        <span className="assist-colors">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`${COLOR_NAMES[c] ?? c} ink`}
              title={`${COLOR_NAMES[c] ?? c} ink`}
              aria-pressed={color === c}
              className={`assist-swatch${color === c ? ' is-active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </span>

        <button type="button" className="btn btn-icon" title="Clear ink" aria-label="Clear ink" onClick={clear}>
          <Eraser size={15} />
        </button>
        {/* Absent where the browser cannot do it at all (iPhone Safari), rather
            than a button that silently does nothing. */}
        {document.fullscreenEnabled && (
          <button
            type="button"
            className="btn btn-icon assist-fullscreen"
            title={fullscreen ? 'Exit full screen' : 'Full screen'}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={toggleFullscreen}
          >
            {fullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
          </button>
        )}
        <button type="button" className="btn btn-danger" onClick={onEnded}>
          <PhoneOff size={14} /> End
        </button>
      </div>

      <div className="assist-screen">
        {/* muted: the operator publishes no audio, and an unmuted video element
            would be blocked from autoplaying by the browser regardless. */}
        <video ref={videoRef} playsInline autoPlay muted />
        <canvas
          ref={canvasRef}
          className={`assist-ink${tool === 'laser' ? ' is-pointer' : ''}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        {!sharing && !error && <p className="assist-placeholder">{status}</p>}
        {error && (
          <p className="assist-placeholder assist-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
